
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const testimonials = [
    {
      name: "Michael Chen",
      location: "Toronto, ON",
      rating: 5,
      text: "Absolutely incredible service! My BMW looks better than the day I bought it. The team was professional, punctual, and the attention to detail was outstanding. Will definitely be using Thercore Mobile Detailing again!",
      vehicle: "BMW 3 Series"
    },
    {
      name: "Sarah Johnson",
      location: "Mississauga, ON", 
      rating: 5,
      text: "I was amazed by the transformation of my SUV's interior. The kids had made quite a mess, but Thercore made it look brand new. The convenience of mobile service is unbeatable!",
      vehicle: "Honda Pilot"
    },
    {
      name: "David Rodriguez",
      location: "Markham, ON",
      rating: 5,
      text: "Professional, reliable, and exceptional results. My Mercedes has never looked better. The paint correction work was flawless and the customer service was top-notch. Highly recommended!",
      vehicle: "Mercedes C-Class"
    },
    {
      name: "Emily Thompson",
      location: "Vaughan, ON",
      rating: 5,
      text: "The combo package was worth every penny. Both interior and exterior detailing exceeded my expectations. The team arrived on time and worked with such precision. My car feels like new again!",
      vehicle: "Audi Q5"
    },
    {
      name: "James Wilson",
      location: "Brampton, ON",
      rating: 5,
      text: "Outstanding service from start to finish. The booking process was easy, and the results speak for themselves. My truck's interior was completely transformed. These guys are true professionals!",
      vehicle: "Ford F-150"
    },
    {
      name: "Lisa Park",
      location: "Richmond Hill, ON",
      rating: 5,
      text: "I've tried other detailing services before, but Thercore is in a league of their own. The attention to detail and quality of work is exceptional. My Lexus has never looked this good!",
      vehicle: "Lexus RX"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d48lt9sgqefflfrp9qeg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-red-600/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-amber-800 rounded-full flex items-center justify-center">
                <i className="ri-car-washing-line text-white text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-white">
                <span style={{ fontFamily: '"Pacifico", serif' }}>Thercore</span>
                <span className="text-red-600 ml-2">Mobile Detailing</span>
              </h1>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('services')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">Gallery</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-white hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              <button onClick={() => scrollToSection('contact')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-600 to-amber-800 text-white hover:from-red-700 hover:to-amber-900 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md">Book Now</button>
            </nav>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white text-2xl cursor-pointer"
            >
              <i className={isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-red-600/20">
              <nav className="flex flex-col space-y-4 mt-4">
                <button onClick={() => scrollToSection('home')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('about')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">About</button>
                <button onClick={() => scrollToSection('services')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">Services</button>
                <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">Gallery</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">Reviews</button>
                <button onClick={() => scrollToSection('contact')} className="text-white hover:text-red-600 transition-colors cursor-pointer text-left whitespace-nowrap">Contact</button>
                <button onClick={() => scrollToSection('contact')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-600 to-amber-800 text-white hover:from-red-700 hover:to-amber-900 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md w-fit">Book Now</button>
              </nav>
            </div>
          )}
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="text-red-600">Luxury</span> Auto Detailing<br />
                <span className="text-3xl md:text-5xl font-light">at Your Doorstep</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Professional mobile detailing service bringing showroom-level cleaning and protection directly to your location
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="tel:647-584-0472" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105 shadow-lg hover:shadow-xl">
                  <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-0472
                </a>
                <button onClick={() => scrollToSection('services')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105">
                  <i className="ri-eye-line mr-2"></i>View Services
                </button>
              </div>
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-red-600 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">100% Mobile</h3>
                  <p className="text-gray-400 text-sm">We come to your location</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-red-600 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Guaranteed</h3>
                  <p className="text-gray-400 text-sm">Satisfaction guaranteed</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-award-line text-red-600 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Professional</h3>
                  <p className="text-gray-400 text-sm">Premium products & equipment</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-red-600 text-2xl"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                  About <span className="text-red-600">Thercore Mobile Detailing</span>
                </h2>
                <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    alt="Professional auto detailing service" 
                    className="rounded-2xl shadow-2xl object-cover w-full h-96 object-top" 
                    src="https://public.readdy.ai/ai/img_res/73a43a2c077681cd56b06a64e7fe89ae.jpg"
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    At Thercore Mobile Detailing, we're committed to delivering exceptional automotive care that exceeds your expectations. Our team of skilled professionals brings years of experience and passion for perfection to every vehicle we service.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We understand that your vehicle is more than just transportation—it's an investment that deserves the highest level of care. That's why we use only premium products and state-of-the-art equipment to ensure your car receives the royal treatment it deserves.
                  </p>
                  <div className="grid grid-cols-2 gap-6 mt-8">
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">500+</div>
                      <div className="text-gray-600">Cars Detailed</div>
                    </div>
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                      <div className="text-gray-600">Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Our <span className="text-red-600">Services</span>
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Choose from our range of professional detailing services, all performed at your location
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Regular Wash */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-600/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-600/20">
                    <i className="ri-car-washing-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Regular Wash</h3>
                  <div className="text-3xl font-bold text-red-600 mb-4">$149.99</div>
                  <p className="text-gray-400 mb-6">Exterior wash, rim and tire cleaning, and light interior wipe-down</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Exterior wash & dry
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Rim & tire cleaning
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Light interior wipe-down
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Window cleaning
                  </li>
                </ul>
                <button onClick={() => scrollToSection('contact')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </button>
              </div>

              {/* Interior Only */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-600/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-600/20">
                    <i className="ri-brush-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Interior Only</h3>
                  <div className="text-3xl font-bold text-red-600 mb-4">Starting at $189.99</div>
                  <p className="text-gray-400 mb-6">Comprehensive interior detailing for all vehicle types</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Sedan - $189.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>SUV 5-seater - $214.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>7-seater & Truck - $229.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Mini Van - $249.99
                  </li>
                </ul>
                <button onClick={() => scrollToSection('contact')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </button>
              </div>

              {/* Showroom Package */}
              <div className="relative bg-gray-900 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-red-600 shadow-2xl shadow-red-600/20">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold">Most Popular</span>
                </div>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-600">
                    <i className="ri-star-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Showroom Package</h3>
                  <div className="text-3xl font-bold text-red-600 mb-4">Starting at $239.99</div>
                  <p className="text-gray-400 mb-6">Complete inside and outside detailing service</p>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Sedan - $239.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>SUV 5-seater - $264.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>7-seater & Truck - $279.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-600 mr-3"></i>Mini Van - $299.99
                  </li>
                </ul>
                <button onClick={() => scrollToSection('contact')} className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-600 to-amber-800 text-white hover:from-red-700 hover:to-amber-900 shadow-lg hover:shadow-xl px-6 py-3 text-base rounded-lg w-full">
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Before & <span className="text-red-600">After Gallery</span>
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                See the incredible transformations we achieve with our professional detailing services
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Interior Deep Clean */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Interior Deep Clean</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/dc7f0d1e3d4ef1a85759633a824897fe.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/65dace0b12a7c4cda0225b9ec5efabf4.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exterior Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Exterior Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SUV Complete Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">SUV Complete Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8609aae2015379ebb1d01a8c1fa67a9a.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://prestigeautodetailing.site/static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/854be7e3dba04760f5d9ed0529753ceb.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paint Restoration */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Paint Restoration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://public.readdy.ai/ai/img_res/e27b3530b67aecabfe947b498885eadc.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://public.readdy.ai/ai/img_res/e4806feb1b6921c3255c9d38baff7521.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 bg-black">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                What Our <span className="text-red-600">Customers Say</span>
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied customers across the GTA
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gray-900 rounded-2xl p-6 border border-gray-700 hover:border-red-600/50 transition-all duration-300 hover:transform hover:scale-105">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">{testimonial.name}</h4>
                      <p className="text-gray-400 text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-red-600 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-gray-700 pt-4">
                    <p className="text-red-600 text-sm font-semibold">
                      <i className="ri-car-line mr-2"></i>{testimonial.vehicle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <div className="inline-flex items-center space-x-8 bg-gray-900 rounded-2xl p-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">4.9/5</div>
                  <div className="text-gray-300">Average Rating</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">500+</div>
                  <div className="text-gray-300">Happy Customers</div>
                </div>
                <div className="w-px h-16 bg-gray-700"></div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">100%</div>
                  <div className="text-gray-300">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
                Book Your <span className="text-red-600">Appointment</span>
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
                Ready to give your vehicle the royal treatment? Call us now or schedule online
              </p>
              <div className="flex justify-center mb-12">
                <div className="bg-white rounded-full p-1 shadow-lg">
                  <a href="tel:647-584-0472" className="px-8 py-4 rounded-full font-semibold transition-all whitespace-nowrap bg-green-500 text-white hover:bg-green-600 shadow-md mr-2">
                    <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-0472
                  </a>
                  <button 
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                    className="px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap text-gray-600 hover:text-black hover:bg-gray-100"
                  >
                    <i className="ri-calendar-line mr-2"></i>Schedule Online
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-black mb-6">Get In Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-phone-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Phone</h4>
                        <a href="tel:647-584-0472" className="text-green-600 hover:text-green-700 font-semibold cursor-pointer">
                          (647) 584-0472
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Email</h4>
                        <p className="text-gray-600">info@thercoremobiledetailing.com</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-map-pin-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Service Area</h4>
                        <p className="text-gray-600">Greater Toronto Area</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-black">Hours</h4>
                        <p className="text-gray-600">Mon-Sat: 8AM-6PM<br />Sun: 10AM-4PM</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <a href="tel:647-584-0472" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors cursor-pointer shadow-lg block text-center">
                      <i className="ri-phone-line mr-3 text-xl"></i>Call Now for Instant Booking
                      <div className="text-sm font-normal mt-1">(647) 584-0472</div>
                    </a>
                  </div>
                </div>
                <div className="bg-black rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Thercore?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-600 mr-3"></i>100% mobile service - we come to you
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-600 mr-3"></i>Professional‑grade products & equipment
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-600 mr-3"></i>Satisfaction guaranteed
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-600 mr-3"></i>Fully insured & bonded
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-600 mr-3"></i>Eco‑friendly cleaning solutions
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-black mb-6">Send Us a Message</h3>
                <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input 
                        id="name" 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm" 
                        placeholder="Your full name" 
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input 
                        id="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm" 
                        placeholder="your@email.com" 
                        type="email" 
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input 
                        id="phone" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm" 
                        placeholder="(647) 123-4567" 
                        type="tel" 
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                      <select 
                        id="service" 
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select a service</option>
                        <option value="Regular Wash">Regular Wash</option>
                        <option value="Interior Only">Interior Only</option>
                        <option value="Showroom Package">Showroom Package</option>
                        <option value="Custom Quote">Custom Quote</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea 
                      id="message" 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4} 
                      maxLength={500} 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm resize-none" 
                      placeholder="Tell us about your vehicle and any specific requirements..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      Sorry, there was an error sending your message. Please try again.
                    </div>
                  )}
                  
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-red-600 to-amber-800 hover:from-red-700 hover:to-amber-900 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-12">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-amber-800 rounded-full flex items-center justify-center">
                    <i className="ri-car-washing-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold">
                    <span style={{ fontFamily: '"Pacifico", serif' }}>Thercore</span>
                    <span className="text-red-600 ml-2">Mobile Detailing</span>
                  </h3>
                </div>
                <p className="text-gray-400 mb-6 max-w-md">
                  Professional mobile auto detailing service bringing luxury car care directly to your doorstep. Serving the Greater Toronto Area with premium products and exceptional service.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-twitter-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-google-line"></i>
                  </a>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-6 text-red-600">Quick Links</h4>
                <ul className="space-y-3">
                  <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer">Home</button></li>
                  <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer">About Us</button></li>
                  <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer">Services</button></li>
                  <li><button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer">Gallery</button></li>
                  <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer">Contact</button></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-6 text-red-600">Contact Info</h4>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-400">
                    <i className="ri-phone-line mr-3 text-red-600"></i>(647) 584-0472
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-mail-line mr-3 text-red-600"></i>info@thercoremobiledetailing.com
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-map-pin-line mr-3 text-red-600"></i>Greater Toronto Area
                  </li>
                  <li className="flex items-center text-gray-400">
                    <i className="ri-time-line mr-3 text-red-600"></i>Mon-Sat: 8AM-6PM
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-gray-400 text-sm mb-4 md:mb-0">© 2025 Thercore Mobile Detailing. All rights reserved.</p>
                <div className="flex items-center space-x-6">
                  <a href="#" className="text-gray-400 hover:text-red-600 text-sm transition-colors cursor-pointer">Privacy Policy</a>
                  <a href="#" className="text-gray-400 hover:text-red-600 text-sm transition-colors cursor-pointer">Terms of Service</a>
                  <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-600 text-sm transition-colors cursor-pointer">Website Builder</a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
